<template>
  <div class="community-page">
    <div class="community-shell">
      <section class="community-top">
        <div class="community-title">
          <p class="eyebrow">COMMUNITY</p>
          <h1>社区讨论</h1>
          <p>围绕漫画更新、剧情分析、同人创作和求漫互助展开交流。</p>
        </div>
        <div class="community-brand">
          <img :src="brandLogo" alt="道晗漫画平台">
          <div>
            <span>今日社区热度</span>
            <strong>{{ totalHeat }}</strong>
          </div>
        </div>
      </section>

      <section class="composer-panel">
        <div class="composer-head">
          <div>
            <span class="small-label">发布讨论</span>
            <h2>和大家聊聊你正在看的漫画</h2>
          </div>
          <button type="button" class="primary-action" :disabled="communityInteractionRestricted" @click="publishPost">
            {{ communityInteractionRestricted ? '青少年模式下不可发帖' : '发布讨论' }}
          </button>
        </div>
        <p v-if="communityInteractionRestricted" class="community-restriction">
          青少年模式下不能发布讨论或发表评论，请先完成成年人密码认证。
        </p>
        <div class="composer-grid">
          <input v-model.trim="draftTitle" type="text" maxlength="40" placeholder="输入讨论标题" :disabled="communityInteractionRestricted">
          <select v-model="draftTopic" :disabled="communityInteractionRestricted">
            <option v-for="topic in postableTopics" :key="topic.id" :value="topic.id">{{ topic.name }}</option>
          </select>
          <textarea v-model.trim="draftContent" maxlength="140" placeholder="写下你的观点、求助或创作说明" :disabled="communityInteractionRestricted"></textarea>
        </div>
        <p class="composer-tip">{{ publishMessage }}</p>
      </section>

      <div class="community-layout">
        <main class="discussion-main">
          <div class="toolbar">
            <div class="topic-tabs">
              <button
                v-for="topic in topicTags"
                :key="topic.id"
                type="button"
                :class="{ active: activeTopic === topic.id }"
                @click="switchTopic(topic.id)"
              >
                {{ topic.name }}
              </button>
            </div>
            <div class="sort-switch">
              <button type="button" :class="{ active: sortMode === 'hot' }" @click="sortMode = 'hot'">热度</button>
              <button type="button" :class="{ active: sortMode === 'new' }" @click="sortMode = 'new'">最新</button>
            </div>
          </div>

          <div v-if="loadingPosts" class="empty-state">帖子加载中...</div>

          <article v-for="post in filteredPosts" :key="post.id" class="post-card">
            <div class="post-body">
              <div class="post-meta">
                <strong>{{ post.author }}</strong>
                <span>社区成员</span>
                <span>{{ formatDate(post.createtime) }}</span>
                <b v-if="post.pinned">置顶</b>
                <b v-if="post.heated" class="hot-badge">热议</b>
              </div>
              <h2>{{ post.title }}</h2>
              <p>{{ post.digest }}</p>
              <div class="tag-row">
                <span v-for="tag in post.tags" :key="tag"># {{ tag }}</span>
              </div>
              <div class="post-actions">
                <button type="button" :class="{ active: post.liked }" @click="toggleLike(post.id)">
                  点赞 {{ post.likes }}
                </button>
                <button type="button" :disabled="communityInteractionRestricted" @click="openComments(post)">
                  {{ communityInteractionRestricted ? '评论已限制' : `评论 ${post.comments || 0}` }}
                </button>
                <span>{{ post.views }} 浏览</span>
              </div>
            </div>
          </article>

          <div v-if="filteredPosts.length === 0" class="empty-state">
            暂时没有匹配的讨论，换个话题看看。
          </div>
        </main>

        <aside class="community-side">
          <section class="side-panel topic-panel">
            <div class="panel-head">
              <h2>热门话题</h2>
              <button type="button" @click="toggleFollow('daily')">{{ followedTopics.includes('daily') ? '已关注' : '关注话题' }}</button>
            </div>
            <div class="topic-list">
              <button
                v-for="topic in visibleTopics"
                :key="topic.id"
                type="button"
                :class="[topic.tone, { active: activeTopic === topic.id }]"
                @click="activeTopic = topic.id"
              >
                <span>{{ topic.name }}</span>
                <strong>{{ topic.count }}</strong>
              </button>
            </div>
          </section>

          <section class="side-panel pinned-panel">
            <h2>社区公告</h2>
            <div v-for="item in pinned" :key="item.id" class="notice-item">
              <strong>{{ item.title }}</strong>
              <p>{{ item.summary }}</p>
            </div>
          </section>

          <section class="side-panel stats-panel">
            <h2>活跃榜</h2>
            <div class="stats-strip">
              <div v-for="item in communityStats" :key="item.label">
                <strong>{{ item.value }}</strong>
                <span>{{ item.label }}</span>
              </div>
            </div>
            <div class="user-rank" v-for="user in activeUsers" :key="user.name">
              <div>
                <strong>{{ user.name }}</strong>
                <small>{{ user.badge }}</small>
              </div>
              <b>{{ user.score }}</b>
            </div>
          </section>
        </aside>
      </div>
    </div>

    <div class="comment-drawer" v-if="selectedPost" @click.self="selectedPost = null">
      <div class="drawer-panel">
        <button type="button" class="drawer-close" @click="selectedPost = null">关闭</button>
        <img :src="placeholderImage" alt="讨论预览">
        <span class="small-label">评论区</span>
        <h2>{{ selectedPost.title }}</h2>
        <p>{{ selectedPost.digest }}</p>

        <div class="drawer-loading" v-if="loadingPostComments">评论加载中...</div>
        <div class="comment-line" v-for="item in postComments" :key="item.id">
          <strong>{{ item.nickname }}</strong>
          <span>{{ item.content }}</span>
          <small>{{ formatDate(item.createtime) }}</small>
          <button v-if="item.is_mine" class="comment-del" @click="deletePostComment(item.id)">删除</button>
        </div>
        <div class="comment-line empty" v-if="!loadingPostComments && postComments.length === 0">
          <span>还没有评论，来抢沙发吧</span>
        </div>

        <div class="drawer-input">
          <input
            v-model.trim="postCommentContent"
            type="text"
            maxlength="300"
            :placeholder="communityInteractionRestricted ? '青少年模式下不能发表评论' : '写下你的评论...'"
            :disabled="communityInteractionRestricted"
            @keyup.enter="submitPostComment"
          >
          <button type="button" :disabled="communityInteractionRestricted" @click="submitPostComment">发送</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import brandLogo from '../assets/daohan-logo.png'
import placeholderImage from '../assets/comic-placeholder.svg'
import api from '../api/index'
import { topicTags, activeUsers, communityStats } from '../data/communityMock'
import { initYouthStore, isYouthCommunityRestricted } from '../utils/youthContent'

export default {
  name: 'CommunityPage',
  data() {
    return {
      brandLogo,
      placeholderImage,
      topicTags,
      activeUsers,
      communityStats,
      posts: [],
      loadingPosts: false,
      activeTopic: 'all',
      sortMode: 'hot',
      draftTitle: '',
      draftTopic: 'hot',
      draftContent: '',
      publishMessage: '发布的内容会保存到服务器，所有用户都能看到。',
      selectedPost: null,
      postComments: [],
      loadingPostComments: false,
      postCommentContent: '',
      followedTopics: JSON.parse(localStorage.getItem('community_followed_topics') || '[]')
    }
  },
  computed: {
    communityInteractionRestricted() {
      return isYouthCommunityRestricted()
    },
    postableTopics() {
      return this.topicTags.filter(topic => topic.id !== 'all')
    },
    visibleTopics() {
      return this.topicTags.filter(topic => topic.id !== 'all')
    },
    filteredPosts() {
      const list = [...this.posts]
      return list.sort((a, b) => {
        if (this.sortMode === 'new') {
          return (b.createtime || b.id) - (a.createtime || a.id)
        }
        return (b.likes + b.views) - (a.likes + a.views)
      })
    },
    totalHeat() {
      return this.posts.reduce((sum, post) => sum + post.likes + post.views, 0).toLocaleString()
    }
  },
  mounted() {
    initYouthStore()
    this.loadPosts()
  },
  methods: {
    formatDate(timestamp) {
      if (!timestamp) return ''
      const date = new Date(timestamp * 1000)
      if (isNaN(date.getTime())) return ''
      const pad = n => String(n).padStart(2, '0')
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`
    },
    async loadPosts() {
      this.loadingPosts = true
      try {
        const res = await api.getCommunityPosts({ topic: this.activeTopic, limit: 30 })
        if (res && res.code === 1 && res.data) {
          this.posts = res.data.list || []
        }
      } catch (error) {
        console.error('加载社区帖子失败:', error)
        this.publishMessage = '帖子加载失败，请刷新重试。'
      } finally {
        this.loadingPosts = false
      }
    },
    switchTopic(id) {
      if (this.activeTopic === id) return
      this.activeTopic = id
      this.loadPosts()
    },
    async publishPost() {
      if (this.communityInteractionRestricted) {
        this.publishMessage = '青少年模式下不能发布讨论，请先完成成年人密码认证。'
        return
      }
      if (!localStorage.getItem('token')) {
        this.$router.push('/login?redirect=/community')
        return
      }
      if (!this.draftTitle || !this.draftContent) {
        this.publishMessage = '请先填写标题和内容。'
        return
      }

      try {
        const res = await api.publishCommunityPost({
          topic: this.draftTopic,
          title: this.draftTitle,
          digest: this.draftContent,
          tags: [this.postableTopics.find(topic => topic.id === this.draftTopic)?.name || '讨论', '新讨论']
        })
        if (res && res.code === 1 && res.data) {
          this.posts = [res.data, ...this.posts]
          this.draftTitle = ''
          this.draftContent = ''
          this.publishMessage = '发布成功，已加入当前讨论流。'
          this.activeTopic = this.draftTopic
          this.sortMode = 'new'
          this.loadPosts()
        } else {
          this.publishMessage = (res && res.msg) || '发布失败，请重试。'
        }
      } catch (error) {
        // 未实名（业务码1101）：引导前往实名认证
        if (error && error.businessCode === 1101) {
          this.publishMessage = '请先完成实名认证后再发布（个人中心 - 设置 - 实名认证）。'
          setTimeout(() => {
            this.$router.push('/user?tab=realname')
          }, 1500)
          return
        }
        this.publishMessage = (error && error.message) || '发布失败，请重试。'
      }
    },
    async toggleLike(id) {
      if (!localStorage.getItem('token')) {
        this.$router.push('/login?redirect=/community')
        return
      }
      const post = this.posts.find(item => item.id === id)
      if (!post) return
      try {
        const res = await api.likeCommunityPost(id)
        if (res && res.code === 1 && res.data) {
          post.liked = res.data.liked
          post.likes = res.data.likes
        }
      } catch (error) {
        this.publishMessage = (error && error.message) || '操作失败，请重试。'
      }
    },
    toggleFollow(id) {
      this.followedTopics = this.followedTopics.includes(id)
        ? this.followedTopics.filter(item => item !== id)
        : [...this.followedTopics, id]
      localStorage.setItem('community_followed_topics', JSON.stringify(this.followedTopics))
    },
    openComments(post) {
      if (this.communityInteractionRestricted) {
        this.publishMessage = '青少年模式下不能发表评论，请先完成成年人密码认证。'
        return
      }
      api.viewCommunityPost(post.id).catch(() => {})
      post.views = (post.views || 0) + 1
      this.selectedPost = post
      this.postCommentContent = ''
      this.loadPostComments(post.id)
    },
    async loadPostComments(postId) {
      this.loadingPostComments = true
      this.postComments = []
      try {
        const res = await api.getPostComments(postId)
        if (res && res.code === 1 && res.data) {
          this.postComments = res.data.list || []
        }
      } catch (error) {
        console.error('加载帖子评论失败:', error)
      } finally {
        this.loadingPostComments = false
      }
    },
    async submitPostComment() {
      if (!localStorage.getItem('token')) {
        this.$router.push('/login?redirect=/community')
        return
      }
      if (!this.postCommentContent) return
      try {
        const res = await api.submitPostComment({
          post_id: this.selectedPost.id,
          content: this.postCommentContent
        })
        if (res && res.code === 1 && res.data) {
          this.postComments = [res.data, ...this.postComments]
          this.postCommentContent = ''
          this.selectedPost.comments = (this.selectedPost.comments || 0) + 1
        } else {
          this.publishMessage = (res && res.msg) || '评论失败，请重试。'
        }
      } catch (error) {
        // 未实名（业务码1101）：引导前往实名认证
        if (error && error.businessCode === 1101) {
          this.publishMessage = '请先完成实名认证后再评论（个人中心 - 设置 - 实名认证）。'
          setTimeout(() => {
            this.$router.push('/user?tab=realname')
          }, 1500)
          return
        }
        this.publishMessage = (error && error.message) || '评论失败，请重试。'
      }
    },
    async deletePostComment(commentId) {
      if (!confirm('确定删除这条评论吗？')) return
      try {
        const res = await api.deletePostComment(commentId)
        if (res && res.code === 1) {
          this.postComments = this.postComments.filter(item => item.id !== commentId)
          this.selectedPost.comments = Math.max(0, (this.selectedPost.comments || 0) - 1)
        } else {
          this.publishMessage = (res && res.msg) || '删除失败'
        }
      } catch (error) {
        this.publishMessage = (error && error.message) || '删除失败'
      }
    }
  }
}
</script>

<style scoped>
.community-page {
  min-height: 100vh;
  padding: 34px 0 64px;
  color: #eef3ff;
  background:
    radial-gradient(circle at 12% 0%, rgba(255, 204, 102, 0.12), transparent 31%),
    radial-gradient(circle at 86% 10%, rgba(35, 211, 238, 0.12), transparent 28%),
    linear-gradient(180deg, rgba(7, 9, 18, 0.22), rgba(7, 9, 18, 0.74));
}

.community-shell {
  width: min(1500px, 100%);
  margin: 0 auto;
  padding: 0 32px;
}

.community-top,
.composer-panel,
.side-panel {
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: linear-gradient(180deg, rgba(20, 24, 39, 0.9), rgba(12, 15, 27, 0.94));
  box-shadow: 0 28px 70px rgba(0, 0, 0, 0.32);
}

.community-top {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 24px;
  align-items: center;
  padding: 28px;
}

.eyebrow,
.small-label {
  margin-bottom: 8px;
  color: #23d3ee;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0;
}

.community-title h1 {
  margin: 0 0 10px;
  color: #f5f7ff;
  font-size: clamp(30px, 3vw, 44px);
  line-height: 1.08;
}

.community-title p:last-child,
.composer-tip,
.post-body p,
.notice-item p {
  color: #9faaca;
  line-height: 1.72;
}

.community-restriction {
  margin: 0 0 16px;
  padding: 12px 14px;
  border: 1px solid rgba(255, 204, 102, 0.42);
  border-radius: 8px;
  background: rgba(255, 204, 102, 0.1);
  color: #ffd98a;
  font-size: 14px;
  line-height: 1.6;
}

.community-brand {
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 260px;
  padding: 16px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.06);
}

.community-brand img {
  width: 58px;
  height: 58px;
  border-radius: 8px;
  object-fit: cover;
  background: rgba(255, 255, 255, 0.92);
}

.community-brand span,
.post-meta span,
.post-actions span,
.user-rank small {
  color: #9faaca;
  font-size: 13px;
}

.community-brand strong {
  display: block;
  margin-top: 4px;
  color: #ffcc66;
  font-size: 28px;
}

.composer-panel {
  margin-top: 22px;
  padding: 22px;
}

.composer-head {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: center;
  margin-bottom: 16px;
}

.composer-head h2,
.side-panel h2 {
  margin: 0;
  color: #f5f7ff;
  font-size: 20px;
}

.primary-action,
.post-actions button,
.sort-switch button,
.topic-tabs button,
.panel-head button,
.drawer-close {
  min-height: 38px;
  border: 0;
  border-radius: 8px;
  padding: 0 14px;
  color: #dfe7ff;
  font-weight: 900;
  cursor: pointer;
  background: rgba(255, 255, 255, 0.08);
  transition: all 0.22s ease;
}

.primary-action,
.post-actions button.active,
.sort-switch button.active,
.topic-tabs button.active,
.panel-head button {
  color: #07101d;
  background: linear-gradient(135deg, #ffcc66, #23d3ee);
}

.composer-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 180px;
  gap: 12px;
}

.composer-grid input,
.composer-grid select,
.composer-grid textarea {
  width: 100%;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  padding: 13px 14px;
  color: #eef3ff;
  outline: none;
  background: rgba(7, 10, 18, 0.82);
}

.composer-grid input:disabled,
.composer-grid select:disabled,
.composer-grid textarea:disabled,
.primary-action:disabled,
.post-actions button:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.composer-grid textarea {
  grid-column: 1 / -1;
  min-height: 92px;
  resize: vertical;
}

.composer-grid input:focus,
.composer-grid select:focus,
.composer-grid textarea:focus {
  border-color: #23d3ee;
  box-shadow: 0 0 0 4px rgba(35, 211, 238, 0.12);
}

.composer-tip {
  margin-top: 12px;
  font-size: 13px;
}

.community-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 360px;
  gap: 22px;
  align-items: start;
  margin-top: 22px;
}

.discussion-main {
  display: grid;
  gap: 14px;
}

.toolbar {
  display: flex;
  justify-content: space-between;
  gap: 14px;
  align-items: center;
  padding: 12px;
  border-radius: 10px;
  background: rgba(5, 8, 16, 0.68);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.topic-tabs,
.sort-switch {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.post-card {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  padding: 20px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: linear-gradient(180deg, rgba(22, 26, 43, 0.96), rgba(11, 14, 25, 0.98));
  box-shadow: 0 22px 48px rgba(0, 0, 0, 0.28);
}

.post-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.post-meta strong,
.user-rank strong,
.notice-item strong {
  color: #f5f7ff;
}

.post-meta b {
  padding: 3px 8px;
  border-radius: 999px;
  color: #07101d;
  font-size: 12px;
  background: #ffcc66;
}

.post-meta .hot-badge {
  background: #23d3ee;
}

.post-body h2 {
  margin: 10px 0 8px;
  color: #f5f7ff;
  font-size: 21px;
  line-height: 1.36;
}

.tag-row,
.post-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 9px;
  align-items: center;
  margin-top: 14px;
}

.tag-row span {
  padding: 5px 9px;
  border-radius: 999px;
  color: #23d3ee;
  font-size: 12px;
  background: rgba(35, 211, 238, 0.12);
}

.community-side {
  display: grid;
  gap: 16px;
  position: sticky;
  top: 18px;
}

.side-panel {
  padding: 18px;
}

.panel-head {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
  margin-bottom: 14px;
}

.topic-list {
  display: grid;
  gap: 9px;
}

.topic-list button {
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 42px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  padding: 0 12px;
  color: #dfe7ff;
  cursor: pointer;
  background: rgba(255, 255, 255, 0.06);
}

.topic-list button.active,
.topic-list button:hover,
.post-actions button:hover,
.topic-tabs button:hover,
.sort-switch button:hover {
  transform: translateY(-1px);
  border-color: rgba(35, 211, 238, 0.42);
}

.topic-list .gold strong { color: #ffcc66; }
.topic-list .cyan strong { color: #23d3ee; }
.topic-list .red strong { color: #ff8a8a; }
.topic-list .green strong { color: #80e0a7; }
.topic-list .blue strong { color: #84b8ff; }
.topic-list .purple strong { color: #b69bff; }

.notice-item {
  padding: 14px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.notice-item:last-child {
  border-bottom: 0;
}

.notice-item p {
  margin-top: 6px;
  font-size: 13px;
}

.stats-strip {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin: 14px 0;
}

.stats-strip div {
  padding: 12px 8px;
  border-radius: 8px;
  text-align: center;
  background: rgba(255, 255, 255, 0.06);
}

.stats-strip strong {
  display: block;
  color: #ffcc66;
  font-size: 20px;
}

.stats-strip span {
  color: #9faaca;
  font-size: 12px;
}

.user-rank {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 10px;
  align-items: center;
  padding: 10px 0;
}

.user-rank div {
  min-width: 0;
}

.user-rank small {
  display: block;
  margin-top: 3px;
}

.user-rank b {
  color: #23d3ee;
}

.empty-state {
  padding: 28px;
  border-radius: 10px;
  color: #9faaca;
  text-align: center;
  background: rgba(255, 255, 255, 0.06);
}

.comment-drawer {
  position: fixed;
  inset: 0;
  z-index: 900;
  display: flex;
  justify-content: flex-end;
  background: rgba(0, 0, 0, 0.66);
}

.drawer-panel {
  width: min(460px, 92vw);
  height: 100%;
  overflow-y: auto;
  padding: 24px;
  color: #eef3ff;
  background: #111525;
  border-left: 1px solid rgba(255, 255, 255, 0.1);
}

.drawer-close {
  float: right;
}

.drawer-panel img {
  width: 100%;
  height: 180px;
  margin: 18px 0;
  border-radius: 8px;
  object-fit: cover;
  background: rgba(255, 255, 255, 0.92);
}

.drawer-panel h2 {
  clear: both;
  margin: 8px 0 10px;
  color: #f5f7ff;
}

.drawer-panel p {
  color: #9faaca;
  line-height: 1.7;
}

.comment-line {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 4px 10px;
  padding: 14px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.comment-line strong {
  color: #f5f7ff;
}

.comment-line span {
  grid-column: 1 / -1;
  color: #9faaca;
  line-height: 1.6;
}

.comment-line small {
  color: #6b7695;
  font-size: 12px;
}

.comment-line.empty span {
  text-align: center;
  color: #6b7695;
}

.comment-del {
  border: 0;
  color: #ff8a80;
  font-size: 12px;
  cursor: pointer;
  background: transparent;
}

.drawer-loading {
  padding: 14px 0;
  color: #6b7695;
  font-size: 13px;
  text-align: center;
}

.drawer-input {
  display: flex;
  gap: 10px;
  margin-top: 16px;
}

.drawer-input input {
  flex: 1;
  min-width: 0;
  height: 42px;
  padding: 0 13px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 8px;
  color: #eef3ff;
  outline: none;
  background: rgba(7, 10, 18, 0.82);
}

.drawer-input button {
  padding: 0 16px;
  border: 0;
  border-radius: 8px;
  color: #07101d;
  font-weight: 900;
  cursor: pointer;
  background: linear-gradient(135deg, #ffcc66, #23d3ee);
}

.drawer-input button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

@media (max-width: 1180px) {
  .community-layout {
    grid-template-columns: 1fr;
  }

  .community-side {
    position: static;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .stats-panel {
    grid-column: 1 / -1;
  }
}

@media (max-width: 720px) {
  .community-shell {
    padding: 0 16px;
  }

  .community-top,
  .composer-head,
  .toolbar {
    grid-template-columns: 1fr;
    display: grid;
  }

  .community-brand {
    min-width: 0;
  }

  .composer-grid,
  .community-side {
    grid-template-columns: 1fr;
  }

  .post-card {
    grid-template-columns: 1fr;
  }
}
</style>
